
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function RoofPaintingPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = 'Roof Painting Cape Town | Roof Restoration & Waterproofing | Professional Roof Contractors';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional roof painting and restoration services in Cape Town. Extend roof life, improve insulation, prevent leaks. Expert roof contractors with quality materials. Free quotes available.');
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'roof painting Cape Town, roof restoration, roof waterproofing, roof contractors, roof maintenance');
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/roof-painting`);
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Roof Painting Services Cape Town",
      "description": "Professional roof painting and restoration services including waterproofing, insulation improvement, and leak prevention for residential and commercial properties.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/roof-painting`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Cape Town Professional Painters",
        "areaServed": "Cape Town, Western Cape, South Africa"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Roof Painting Services Cape Town')) {
        existingScript.remove();
      }
    };
  }, []);

  const roofServices = [
    {
      title: 'Tile Roof Painting',
      description: 'Restore and protect concrete and clay tile roofs with specialized coatings.',
      features: ['Tile cleaning', 'Crack sealing', 'Primer application', 'Protective topcoat']
    },
    {
      title: 'Metal Roof Coating',
      description: 'Extend the life of metal roofs with rust prevention and thermal coatings.',
      features: ['Rust treatment', 'Thermal barriers', 'Corrosion protection', 'Energy efficiency']
    },
    {
      title: 'Flat Roof Waterproofing',
      description: 'Complete waterproofing solutions for flat and low-slope commercial roofs.',
      features: ['Membrane application', 'Drainage solutions', 'Leak detection', 'Maintenance plans']
    },
    {
      title: 'Roof Restoration',
      description: 'Comprehensive restoration services to bring old roofs back to life.',
      features: ['Damage assessment', 'Structural repairs', 'Complete refinishing', 'Warranty coverage']
    }
  ];

  const roofBenefits = [
    {
      title: 'Extended Roof Life',
      description: 'Quality roof painting can extend your roof\'s lifespan by 10-15 years.',
      icon: 'ri-time-line'
    },
    {
      title: 'Energy Savings',
      description: 'Reflective coatings reduce heat absorption, lowering cooling costs significantly.',
      icon: 'ri-leaf-line'
    },
    {
      title: 'Leak Prevention',
      description: 'Protective coatings seal minor cracks and prevent water infiltration.',
      icon: 'ri-shield-check-line'
    },
    {
      title: 'Property Value',
      description: 'A well-maintained roof increases property value and curb appeal.',
      icon: 'ri-home-heart-line'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/13ce4430240586267763417836141225.jpg"
            alt="Professional roof painting services in Cape Town"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Professional <span className="text-yellow-400">Roof Painting</span> Services
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Protect and beautify your roof with expert roof painting services in Cape Town. Our contractors provide durable coatings that withstand harsh weather conditions.
            </p>
            <div className="flex gap-4">
              <Link
                to="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </Link>
              <Link
                to="/services"
                className="bg-gray-100 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Roof Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              Comprehensive Roof Painting Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              From residential tile roofs to commercial flat roofs, our network of roof specialists delivers protective coatings that extend roof life and improve performance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {roofServices.map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-gray-100">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                
                <div className="grid grid-cols-2 gap-3">
                  {service.features.map((feature, idx) => (
                    <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                      <i className={`${feature.icon} text-black text-2xl`}></i>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roof Benefits */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Benefits of Professional Roof Painting
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Invest in your roof's future with professional painting that delivers lasting protection and value
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {roofBenefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow border border-gray-100">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${benefit.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-black mb-3">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roof Types */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Roof Types We Service
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our contractors have expertise across all common roof types found in Cape Town
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-home-8-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Tile Roofs</h3>
              <p className="text-gray-600">Concrete and clay tile roofs with specialized cleaning, sealing, and protective coating systems.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-shield-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Metal Roofs</h3>
              <p className="text-gray-600">Steel and aluminum roofing with rust treatment, thermal barriers, and long-lasting protective coatings.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-temp-cold-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Flat Roofs</h3>
              <p className="text-gray-600">Commercial and residential flat roofs with waterproof membrane systems and drainage solutions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Our Roof Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Systematic approach ensuring maximum protection and longevity for your roof investment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {[
              {
                step: '01',
                title: 'Roof Inspection',
                description: 'Comprehensive assessment of roof condition, damage, and repair needs.'
              },
              {
                step: '02',
                title: 'Cleaning & Preparation',
                description: 'High-pressure cleaning, moss removal, and surface preparation.'
              },
              {
                step: '03',
                title: 'Repairs & Sealing',
                description: 'Fix cracks, replace damaged tiles, and seal all penetrations.'
              },
              {
                step: '04',
                title: 'Primer & Coating',
                description: 'Apply specialized primer and protective coating systems.'
              },
              {
                step: '05',
                title: 'Quality Assurance',
                description: 'Final inspection and warranty documentation for peace of mind.'
              }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-6 text-lg font-bold">
                  {process.step}
                </div>
                <h4 className="text-lg font-bold text-black mb-4">{process.title}</h4>
                <p className="text-gray-600 leading-relaxed text-sm">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety & Warranty */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Safety & Warranty Commitment
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Safety First</h3>
              <p className="text-gray-600 leading-relaxed">
                All our contractors are fully insured and follow strict safety protocols. Professional equipment and safety gear ensure secure roof access and work completion.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-line text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Quality Warranty</h3>
              <p className="text-gray-600 leading-relaxed">
                Comprehensive warranties on materials and workmanship give you confidence in your investment. Our contractors stand behind their work with guaranteed results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Related Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete your property transformation with our comprehensive painting solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Link
              to="/services/exterior-painting"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-building-2-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Exterior Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Complete exterior solutions</p>
            </Link>

            <Link
              to="/services/waterproofing"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-droplet-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Waterproofing
              </h3>
              <p className="text-gray-600 text-sm mt-2">Advanced moisture protection</p>
            </Link>

            <Link
              to="/services/commercial-painting"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-community-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Commercial Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Business roof solutions</p>
            </Link>

            <Link
              to="/services/interior-painting"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-home-4-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Indoor Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Indoor transformations</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-r from-yellow-400 to-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">
            Ready to Protect Your Roof Investment?
          </h2>
          <p className="text-xl text-black/80 mb-12 max-w-4xl mx-auto leading-relaxed">
            Connect with Cape Town's most trusted roof painting contractors through our platform. Get up to 5 quotes and choose the perfect contractor for your roof project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
